import { useRef, useState, useEffect } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  AnimatePresence,
  animate,
  useReducedMotion,
} from "framer-motion";

interface Phone3DProps {
  children: React.ReactNode;
  /**
   * Destination opened by the hover/tap CTA. If omitted, no CTA overlay is rendered.
   */
  href?: string;
  /**
   * Label shown in the hover/tap overlay. Defaults to "Try Example".
   */
  ctaLabel?: string;
}

/**
 * Detect whether the primary input is a fine-pointer with hover capability
 * (i.e. a mouse, not a touchscreen). Touch browsers emulate mouse events
 * unpredictably, which made the 3D tilt animation flail; we disable the
 * tracking + idle-drift effects entirely on touch.
 */
const useHoverCapable = () => {
  const [capable, setCapable] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    setCapable(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setCapable(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return capable;
};

/**
 * Phone mockup wrapper with a hover/tap CTA overlay.
 *
 * On hover-capable devices (mouse + fine pointer), the phone also gets the
 * full 3D tilt treatment — perspective rotation that tracks the mouse,
 * idle drift when the mouse isn't moving, and a specular highlight.
 *
 * On touch devices (and when prefers-reduced-motion is set), the 3D
 * effects are skipped entirely because emulated touch-mouse events fight
 * with the spring physics. The CTA overlay still works — it appears on
 * tap and dismisses on tap-outside.
 *
 * Extracted from LandingHero so multiple marketing surfaces (the home
 * page hero, the use-case case studies) can reuse the same treatment.
 */
export const Phone3D = ({
  children,
  href,
  ctaLabel = "Try Example",
}: Phone3DProps) => {
  const reducedMotion = useReducedMotion();
  const hoverCapable = useHoverCapable();
  const interactive = hoverCapable && !reducedMotion;

  const ref = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const mouseX = useMotionValue(0.15);
  const mouseY = useMotionValue(-0.08);

  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [14, -14]), {
    stiffness: 120,
    damping: 18,
  });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-14, 14]), {
    stiffness: 120,
    damping: 18,
  });

  const shadowX = useTransform(rotateY, [-14, 14], [25, -25]);
  const shadowY = useTransform(rotateX, [-14, 14], [-20, 40]);
  const shadowFilter = useTransform(
    [shadowX, shadowY],
    ([sx, sy]) =>
      `drop-shadow(${sx}px ${sy}px 40px rgba(17,17,24,0.3)) drop-shadow(0px 8px 16px rgba(17,17,24,0.15))`,
  );

  const specularBg = useTransform([mouseX, mouseY], ([mx, my]) => {
    const x = 50 + (mx as number) * 60;
    const y = 30 + (my as number) * 40;
    return `radial-gradient(ellipse at ${x}% ${y}%, rgba(255,255,255,0.18) 0%, transparent 55%)`;
  });
  const leftEdgeOpacity = useTransform(rotateY, [-14, 0, 14], [0, 0.3, 0.8]);
  const rightEdgeOpacity = useTransform(rotateY, [-14, 0, 14], [0.8, 0.3, 0]);

  const handleMouse = (e: React.MouseEvent) => {
    if (!interactive) return;
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    mouseX.set((e.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((e.clientY - rect.top) / rect.height - 0.5);
    if (!hovered) setHovered(true);
  };

  const handleLeave = () => {
    if (!interactive) return;
    setHovered(false);
  };

  // Touch-mode tap: reveal the overlay. stopPropagation so the same tap
  // doesn't immediately trigger the document-level click-away listener.
  const handleTap = (e: React.MouseEvent) => {
    if (interactive || !href) return;
    e.stopPropagation();
    setHovered(true);
  };

  const handleFocus = () => {
    if (!href) return;
    setHovered(true);
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      setHovered(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!href) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (hovered) {
        window.open(href, "_blank", "noopener,noreferrer");
      } else {
        setHovered(true);
      }
    } else if (e.key === "Escape") {
      setHovered(false);
    }
  };

  useEffect(() => {
    if (!interactive || hovered) return;
    const ctrlX = animate(mouseX, [0.15, -0.1, 0.12, -0.08, 0.15], {
      duration: 8,
      ease: "easeInOut",
      repeat: Infinity,
    });
    const ctrlY = animate(mouseY, [-0.08, 0.06, -0.1, 0.04, -0.08], {
      duration: 10,
      ease: "easeInOut",
      repeat: Infinity,
    });
    return () => {
      ctrlX.stop();
      ctrlY.stop();
    };
  }, [interactive, hovered, mouseX, mouseY]);

  // Touch-mode dismiss: tapping anywhere outside the phone clears the overlay.
  useEffect(() => {
    if (interactive || !hovered) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setHovered(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [interactive, hovered]);

  const ctaOverlay = (
    <AnimatePresence>
      {hovered && href && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="absolute inset-0 rounded-[44px] z-20 flex items-center justify-center bg-fwd-ink/40 backdrop-blur-[2px]"
        >
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-fwd-red text-white text-sm font-semibold tracking-wider uppercase px-7 py-3.5 rounded-full shadow-lg hover:opacity-90 transition-opacity"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className="shrink-0"
            >
              <path
                d="M6 3.5L10.5 8L6 12.5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2.5 8H10"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
            {ctaLabel}
          </a>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Non-interactive (touch / reduced-motion): no 3D, no springs, no idle
  // drift. Tap reveals the same CTA overlay used on hover for desktop.
  if (!interactive) {
    return (
      <div ref={ref} className="relative">
        <div
          onClick={handleTap}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          tabIndex={href ? 0 : -1}
          className={`relative ${href ? "cursor-pointer" : ""}`}
          style={{
            filter:
              "drop-shadow(0px 20px 30px rgba(17,17,24,0.18)) drop-shadow(0px 6px 12px rgba(17,17,24,0.12))",
          }}
        >
          {children}
          {ctaOverlay}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      onMouseMove={handleMouse}
      onMouseLeave={handleLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      tabIndex={href ? 0 : -1}
      style={{ perspective: 800 }}
      className="relative"
    >
      <motion.div
        style={{
          rotateX,
          rotateY,
          transformStyle: "preserve-3d",
          filter: shadowFilter,
        }}
        className="relative"
      >
        {children}
        <motion.div
          className="absolute inset-0 rounded-[44px] pointer-events-none z-10"
          style={{ background: specularBg }}
        />
        <motion.div
          className="absolute left-0 top-[5%] bottom-[5%] w-[3px] rounded-full pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, transparent, rgba(255,255,255,0.15), transparent)",
            opacity: leftEdgeOpacity,
          }}
        />
        <motion.div
          className="absolute right-0 top-[5%] bottom-[5%] w-[3px] rounded-full pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, transparent, rgba(0,0,0,0.25), transparent)",
            opacity: rightEdgeOpacity,
          }}
        />
        {ctaOverlay}
      </motion.div>
    </div>
  );
};
